import React, {
    useState,
    useRef,
    forwardRef,
    useImperativeHandle,
    useCallback,
    useEffect,
} from "react";
import styled from "styled-components";
import debounce from "lodash/debounce";
import moment from "moment";
import { SearchIcon } from "../assets/svg/SearchIcon";

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SearchContainer = styled.div<{ mode: "modal" | "expand" }>`
    display: flex;
    align-items: center;
    height: 36px;
    padding: 10px;
    border-radius: 4px;
    gap: 10px;
    margin-right: 10px;
    background: ${({ mode }) => (mode === "modal" ? "#ffffff10" : "#ffffff")};
    border: ${({ mode }) =>
        mode === "modal" ? "1px solid #ffffff20" : "1px solid #95abd3"};
    width: ${({ mode }) => (mode === "modal" ? "285px" : "375px")};
`;

const StyledInput = styled.input<{ mode: "modal" | "expand" }>`
    width: 100%;
    font-size: 14px;
    border: none;
    outline: none;
    background: none;

    ::placeholder {
        color: ${({ mode }) => (mode === "modal" ? "#FFFFFF80" : "#9D9D9D")};
    }

    &:focus {
        border: none;
        outline: none;
        box-shadow: none;
    }
`;

interface Column {
    dataIndex?: string;
    title?: string;
    key?: string;
    render?: (text: string, record: any) => React.ReactNode;
}

interface SearchProps {
    data: any[] | undefined;
    columns: Column[];
    onFilter: (filteredData: any[]) => void;
    onSearchTermChange: (searchTerm: string) => void;
    mode?: "modal" | "expand";
    className?: string;
}

const dateFormatColumns = ["updatedAt", "createdAt", "updatedat", "updated_at"];

const formatColumnValue = (columnKey: string, item: any, mode?: "modal" | "expand") => {
    if (dateFormatColumns.includes(columnKey)) {
        if (mode == "modal" && columnKey == "updatedAt" && !item[columnKey] ){
            return item["createdAt"]
            ? moment(item["createdAt"]).local().format("DD-MM-YYYY hh:mm a")
            : "-";
        } else {
            return item[columnKey]
            ? moment(item[columnKey]).local().format("DD-MM-YYYY hh:mm a")
            : "-";
        }
    }
    switch (columnKey) {
        case "envType":
            return item?.deploy?.envType || "";
        case "deployedTime":
            return item?.deploy?.deployedTime
                ? moment.utc(item?.deploy?.deployedTime).fromNow()
                : "";
        case "depolyedBy":
            return item?.deploy?.depolyedBy || "";
        case "formVersion":
            return item.isV2 === false ? "Form v1" : `Form ${item.coreVersion}`;
        default:
            return String(item[columnKey] ?? "").toLowerCase();
    }
};

const SearchBar = forwardRef(
    (
        {
            data,
            columns,
            onFilter,
            onSearchTermChange,
            mode = "expand",
            className = "",
        }: SearchProps,
        ref
    ) => {
        const [searchValue, setSearchValue] = useState<string>("");
        const inputRef = useRef<HTMLInputElement>(null);

        useEffect(() => {
            const handleKeyDown = (event: KeyboardEvent) => {
                if (
                    (event.ctrlKey || event.metaKey) &&
                    event.key === "f"
                ) {
                    event.preventDefault();
                    inputRef.current?.focus();
                }
            };

            document.addEventListener("keydown", handleKeyDown);
            return () => {
                document.removeEventListener("keydown", handleKeyDown);
            };
        }, []);

        const handleSearch = useCallback(
            debounce((value: string) => {
                const searchTerm = value.toLowerCase();
                onSearchTermChange(searchTerm);
                if (!searchTerm || !data) {
                    onFilter(data || []);
                    return;
                }

                const filteredData = data.filter((item) =>
                    columns.some((column) => {
                        if (!column.dataIndex) return false;
                        const itemValue = formatColumnValue(
                            column.dataIndex,
                            item,
                            mode
                        );
                        return itemValue
                            .toLowerCase()
                            .includes(searchTerm.trim().toLowerCase());
                    })
                );

                onFilter(filteredData);
            }, 350),
            [data, columns, onFilter, onSearchTermChange]
        );

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setSearchValue(value);
            handleSearch(value);
        };

        const clearSearch = () => {
            setSearchValue("");
            onSearchTermChange("");
            onFilter(data || []);
        };

        useImperativeHandle(ref, () => ({ clearSearch }));

        return (
            <Container className={`searchBarWrapper ${className}`}>
                <SearchContainer mode={mode}>
                    <SearchIcon />
                    <StyledInput                        
                        ref={inputRef}
                        mode={mode}
                        required
                        type="search"
                        id="search"
                        placeholder="Search"
                        value={searchValue}
                        onChange={handleChange}
                    />
                </SearchContainer>
            </Container>
        );
    }
);

export default SearchBar;

// Highlight match function
export function highlightMatch(
    text: string,
    searchTerm: string
): React.ReactNode {
    if (!text || !searchTerm) return text;

    // Escape special regex characters
    const escapedSearchTerm = searchTerm.replace(
        /[-[\]{}()*+?.,\\^$|#\s]/g,
        "\\$&"
    );

    const regex = new RegExp(`(${escapedSearchTerm})`, "gi");

    return String(text)
        .split(regex)
        .map((part, index) =>
            part.toLowerCase() === searchTerm.toLowerCase() ? (
                <b
                    style={{
                        background: "#f2e055",
                        borderRadius: "2px",
                        padding: "0px 2px",
                    }}
                    key={index}
                >
                    {part}
                </b>
            ) : (
                part
            )
        );
}
